'use client';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useFloating, autoUpdate, useDismiss, useInteractions } from '@floating-ui/react';
import { useLocale, useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import Link from 'next/link';

import ArrowTopRightIcon from '@/assets/icons/arrow-top-right.svg';
import OptionsIcon from '@/assets/icons/options.svg';
import SeniwaveIcon from '@/assets/icons/seniwave.svg';

import Toggle from '@/ui/components/Toggle';

import button from '@/styles/button.module.sass';
import styles from './Footer.module.sass';

export default function Footer() {
  const router = useRouter();
  const t = useTranslations('Footer');

  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'top',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const locale = useLocale();
  const setLocale = (next: 'en' | 'ru') => {
    document.cookie = `locale=${next}; path=/; max-age=31536000`;
    router.refresh();
  };

  const { theme, setTheme } = useTheme();

  return (
    <div className={styles.footer}>
      <Link href="/legal" className={button.linkText}>
        {t('legalPrivacy')}
        <ArrowTopRightIcon width={8} height={8} />
      </Link>
      <Link href="https://seniwave.com/meets" className={button.linkText} target="_blank">
        {t('poweredBy')}
        <SeniwaveIcon width={45} height={18} />
        <ArrowTopRightIcon width={8} height={8} />
      </Link>
      <div className={styles.divider} />

      <button
        className={button.linkText}
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={styles.optionsLabel}>{t('options')}</span>
        <OptionsIcon width={17} height={17} />
      </button>

      {isOpen && (
        <div
          className={styles.options}
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <Toggle
            label={t('theme')}
            value={theme === 'dark' ? 'dark' : 'light'}
            onChange={setTheme}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
          <Toggle
            label={t('language')}
            value={locale === 'ru' ? 'ru' : 'en'}
            onChange={setLocale}
            options={[
              { value: 'en', label: 'English' },
              { value: 'ru', label: 'Русский' },
            ]}
          />
        </div>
      )}
    </div>
  );
}
